import { useEffect, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Share, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useA2HS } from '@/hooks/useA2HS';
import {
  dismissInstallPrompt,
  readState,
  registerVisit,
  shouldShowInstallPrompt,
  snoozeInstallPrompt,
} from '@/lib/installPrompt';

const IOS_STEPS = [
  <>
    Tryck på delningsknappen <Share className="mx-0.5 inline h-4 w-4 align-[-3px]" /> i Safaris
    nedre meny
  </>,
  <>
    Scrolla ner och välj <strong className="text-foreground">”Lägg till på hemskärmen”</strong>
  </>,
  <>
    Tryck <strong className="text-foreground">”Lägg till”</strong> uppe till höger
  </>,
];

/**
 * InstallPrompt — engångs-sheet som föreslår "Lägg till på hemskärmen".
 *
 * Visas tidigast vid andra besöket för inloggade användare, aldrig i
 * standalone-läge och bara där installation är möjlig: Android/Chrome
 * (nativ prompt via useA2HS) eller iOS/Safari (manuell guide, eftersom
 * Safari saknar nativ prompt). "Inte nu" snoozar, X avfärdar permanent —
 * se lib/installPrompt.ts för beslutslogiken.
 */
export function InstallPrompt() {
  const { isInstallable, isIOS, isAlreadyInstalled, promptInstall } = useA2HS();
  const [open, setOpen] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    registerVisit(localStorage, sessionStorage);
  }, []);

  // isInstallable sätts först när beforeinstallprompt fyrar — därav effekten.
  useEffect(() => {
    if (isAlreadyInstalled || (!isInstallable && !isIOS)) return;
    if (shouldShowInstallPrompt(readState(localStorage), Date.now())) setOpen(true);
  }, [isAlreadyInstalled, isInstallable, isIOS]);

  const close = () => setOpen(false);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }
    const result = await promptInstall();
    if (result.outcome === 'accepted') {
      dismissInstallPrompt(localStorage);
    } else {
      snoozeInstallPrompt(localStorage, Date.now());
    }
    close();
  };

  const handleSnooze = () => {
    snoozeInstallPrompt(localStorage, Date.now());
    close();
  };

  const handleDismiss = () => {
    dismissInstallPrompt(localStorage);
    close();
  };

  return (
    <DialogPrimitive.Root
      open={open}
      // Svep/escape/overlay = "Inte nu", inte permanent avfärdande
      onOpenChange={(next) => {
        if (!next) handleSnooze();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-background/60 backdrop-blur-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed inset-x-0 bottom-0 z-50 mx-auto max-w-lg rounded-t-3xl glass-thick px-6 pt-3 pb-[calc(1.5rem+env(safe-area-inset-bottom))] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom duration-300"
          aria-describedby={undefined}
        >
          {/* Drag handle */}
          <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-[hsl(var(--foreground)/0.2)]" />

          <button
            onClick={handleDismiss}
            aria-label="Visa inte igen"
            className="absolute right-4 top-4 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-[hsl(var(--foreground)/0.08)]"
          >
            <X className="h-4 w-4" />
          </button>

          <img
            src="/icon-192x192.png"
            alt=""
            className="mx-auto mb-3 h-14 w-14 rounded-2xl shadow-lg"
          />

          {showIOSGuide ? (
            <>
              <DialogPrimitive.Title className="text-center text-lg font-semibold tracking-tight">
                Så lägger du till appen
              </DialogPrimitive.Title>
              <ol className="mx-auto mt-4 mb-6 flex max-w-xs flex-col gap-3">
                {IOS_STEPS.map((step, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--foreground)/0.08)] text-xs font-bold text-foreground">
                      {i + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
              <Button className="w-full rounded-full" onClick={handleSnooze}>
                Jag förstår
              </Button>
            </>
          ) : (
            <>
              <DialogPrimitive.Title className="text-center text-lg font-semibold tracking-tight">
                Lägg till på hemskärmen
              </DialogPrimitive.Title>
              <p className="mx-auto mt-1.5 mb-5 max-w-xs text-center text-sm text-muted-foreground">
                Installera Ziplink Åtkomst så har du dina nycklar ett tryck bort — utan att öppna
                webbläsaren.
              </p>
              <Button className="w-full rounded-full" onClick={handleInstall}>
                Lägg till
              </Button>
              <Button variant="ghost" className="mt-1 w-full rounded-full" onClick={handleSnooze}>
                Inte nu
              </Button>
            </>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
